import { css } from '@emotion/react';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Collapse,
  Tooltip,
  Box,
  Typography,
  CircularProgress,
  Link as MuiLink,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import INodeAgent, { IWorkerSummary } from 'types/INodeAgent';
import NodeActionsMenu from './NodeActionsMenu';
import NodeDrainDialog, { describeDrain } from './NodeDrainDialog';
import deleteNodeAgent from 'store/deleteNodeAgent';
import sendNodeCommand from 'store/sendNodeCommand';
import useProjectsAsync from 'store/useProjectsAsync';
import DisposeWorkerDialog from './DisposeWorkerDialog';
import ProjectFilterCell from './ProjectFilterCell';
import RetireWorkerDialog from './RetireWorkerDialog';
import RecycleLocalFilesDialog from './RecycleLocalFilesDialog';
import ResizeWorkerDialog from './ResizeWorkerDialog';
import EvictWorkerTasksDialog from './EvictWorkerTasksDialog';
import StopWorkerDialog from './StopWorkerDialog';
import UpgradeWorkerDialog, { UpgradeMode } from './UpgradeWorkerDialog';
import { groupMembers, groupParamsOf, workerDrainSurplus } from 'utils/workerGroup';
import RunningTasksDrawer from './RunningTasksDrawer';
import WorkerActionsMenu from './WorkerActionsMenu';
import { errorMessage } from './errorMessage';
import SlotsCell from './SlotsCell';
import { slotBudgetOf } from 'utils/slotBudget';

interface NodeTableProps {
  nodes: INodeAgent[];
  onStartWorker: (nodeId: string, template?: IWorkerSummary) => void;
  provisioningNodeId: string | null;
  onProvisioning?: (nodeId: string) => void;
  onMutate: () => void;
}

function formatLastSeen(lastSeen: string): string {
  if (!lastSeen) return 'Never';
  const date = new Date(lastSeen);
  if (isNaN(date.getTime())) return 'Invalid';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const time = date.toLocaleTimeString(undefined, { hour12: false });
  return `${y}-${m}-${d} ${time}`;
}

function describeSoftware(worker: IWorkerSummary): { label: string; origin: string | null; tooltip: string } {
  if (worker.software_ref) {
    const target = worker.target ? ` for ${worker.target}` : '';
    const resolved = worker.software_ref !== worker.software ? `, resolved from ${worker.software_ref}` : '';
    return {
      label: worker.advertised_software ?? worker.software ?? worker.software_ref,
      origin: 'Depot',
      tooltip: worker.software ? `Depot release${target}${resolved}` : 'Depot ref, not yet provisioned',
    };
  }
  if (worker.package_name) {
    const version = worker.advertised_software?.split('@')[1] ?? worker.installed_version ?? worker.package_version;
    return {
      label: worker.advertised_software ?? (version ? `${worker.package_name}@${version}` : worker.package_name),
      origin: 'Gemfury',
      tooltip: 'Gemfury package',
    };
  }
  return { label: worker.advertised_software ?? '-', origin: null, tooltip: '' };
}

export default function NodeTable({
  nodes,
  onStartWorker,
  provisioningNodeId,
  onProvisioning,
  onMutate,
}: NodeTableProps) {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [runningTasksTarget, setRunningTasksTarget] = useState<{
    workerId: string;
    workerName: string | null;
  } | null>(null);
  const [cleanupTarget, setCleanupTarget] = useState<{
    nodeAgentId: string;
    instanceId: string;
    workerName: string;
  } | null>(null);
  const [removeTarget, setRemoveTarget] = useState<{
    nodeAgentId: string;
    instanceId: string;
    workerName: string;
  } | null>(null);
  const [recycleTarget, setRecycleTarget] = useState<{ nodeAgentId: string; nodeName: string } | null>(null);
  const [drainTarget, setDrainTarget] = useState<{ node: INodeAgent; mode: 'drain' | 'resume' } | null>(null);
  const [retireTarget, setRetireTarget] = useState<{
    workerId: string;
    workerName: string;
    packageName?: string;
    packageVersion?: string;
    trackingKey: string;
  } | null>(null);
  const [retiringWorkers, setRetiringWorkers] = useState<Set<string>>(new Set());
  const retiringTimestamps = useRef<Map<string, number>>(new Map());
  const [cleaningUpWorkers, setCleaningUpWorkers] = useState<Set<string>>(new Set());
  const [stoppingWorkers, setStoppingWorkers] = useState<Set<string>>(new Set());
  const [stopTarget, setStopTarget] = useState<{
    nodeAgentId: string;
    instanceId: string;
    workerName: string;
  } | null>(null);
  const [removingWorkers, setRemovingWorkers] = useState<Set<string>>(new Set());
  const [removingNodes, setRemovingNodes] = useState<Set<string>>(new Set());
  const [restartingWorkers, setRestartingWorkers] = useState<Set<string>>(new Set());
  const [actionErrors, setActionErrors] = useState<Map<string, string>>(new Map());
  const [upgradingWorkers, setUpgradingWorkers] = useState<Map<string, string | undefined>>(new Map());
  const [upgradeTarget, setUpgradeTarget] = useState<{
    nodeAgentId: string;
    instanceId: string;
    workerName: string;
    currentPackageName?: string;
    currentVersion?: string;
    currentSoftware?: string;
    currentSoftwareRef?: string;
    workdirName?: string;
    worker?: IWorkerSummary;
  } | null>(null);
  const [resizingWorkers, setResizingWorkers] = useState<Map<string, number>>(new Map());
  const [resizeTarget, setResizeTarget] = useState<{
    workerId: string;
    workerName: string;
    currentSlots: number;
    heldSlots?: number;
    replacing?: string[];
    slotBudget: ReturnType<typeof slotBudgetOf>;
    currentOvershoot?: number | null;
    overshootSupported: boolean;
  } | null>(null);
  const [evictingWorkers, setEvictingWorkers] = useState<Set<string>>(new Set());
  const [evictTarget, setEvictTarget] = useState<{
    workerId: string;
    workerName: string;
    evictCount: number;
    isRetiring: boolean;
  } | null>(null);

  const projectsAsync = useProjectsAsync();
  const projectNamesById = useMemo(() => {
    const map = new Map<string, string>();
    for (const project of projectsAsync.data ?? []) map.set(project.id, project.name);
    return map;
  }, [projectsAsync.data]);
  const projectsLoaded = projectsAsync.status === 'SUCCESS' || projectsAsync.status === 'REFRESH';

  // Auto-expand the node that is being provisioned. Adjusting state during
  // render per the React docs' previous-prop pattern — only fires on the
  // transition, leaving manual collapses by the user intact.
  const [prevProvisioningNodeId, setPrevProvisioningNodeId] = useState(provisioningNodeId);
  if (prevProvisioningNodeId !== provisioningNodeId) {
    setPrevProvisioningNodeId(provisioningNodeId);
    if (provisioningNodeId && !expandedNodes.has(provisioningNodeId)) {
      setExpandedNodes((prev) => new Set(prev).add(provisioningNodeId));
    }
  }

  // Clear upgradingWorkers entries once the upgrade completes.
  // We track the installed_version (the frozen `software` pin for a Depot
  // worker) at the time the upgrade was initiated; once it changes (or the
  // status becomes UPGRADE_FAILED), we know the upgrade finished and can clear
  // the local flag. Same render-time previous-prop pattern, keyed on the nodes
  // snapshot from the server.
  const [prevNodesForUpgrade, setPrevNodesForUpgrade] = useState(nodes);
  if (prevNodesForUpgrade !== nodes) {
    setPrevNodesForUpgrade(nodes);
    if (upgradingWorkers.size > 0) {
      const confirmed: string[] = [];
      for (const node of nodes) {
        for (const ws of node.worker_summaries || []) {
          if (!ws.instance_id) continue;
          const key = `${node.id}:${ws.instance_id}`;
          if (!upgradingWorkers.has(key)) continue;
          const oldVersion = upgradingWorkers.get(key);
          const currentVersion = ws.software || ws.installed_version || ws.package_version;
          if (ws.status === 'UPGRADE_FAILED' || (oldVersion && currentVersion && currentVersion !== oldVersion)) {
            confirmed.push(key);
          }
        }
      }
      if (confirmed.length > 0) {
        setUpgradingWorkers((prev) => {
          const next = new Map(prev);
          for (const key of confirmed) next.delete(key);
          return next;
        });
      }
    }
  }

  // Fallback: clear all upgrading entries after 30s.
  // Handles the case where upgrade completes between data refreshes
  // (frontend never sees UPGRADING status).
  useEffect(() => {
    if (upgradingWorkers.size === 0) return;
    const timer = setTimeout(() => setUpgradingWorkers(new Map()), 30000);
    return () => clearTimeout(timer);
  }, [upgradingWorkers.size]);

  // Clear resizingWorkers once the server reflects the requested slot count.
  // Keyed by worker_id (the PATCH target); the backend reports slots from the
  // same slot target the PATCH set, so it matches on the next refresh. Same
  // render-time previous-prop pattern as the upgrade reconcile above.
  const [prevNodesForResize, setPrevNodesForResize] = useState(nodes);
  if (prevNodesForResize !== nodes) {
    setPrevNodesForResize(nodes);
    if (resizingWorkers.size > 0) {
      const confirmed: string[] = [];
      for (const node of nodes) {
        for (const ws of node.worker_summaries || []) {
          if (!ws.worker_id || !resizingWorkers.has(ws.worker_id)) continue;
          const requested = resizingWorkers.get(ws.worker_id);
          if (ws.slots === requested || ws.budget === requested) confirmed.push(ws.worker_id);
        }
      }
      if (confirmed.length > 0) {
        setResizingWorkers((prev) => {
          const next = new Map(prev);
          for (const key of confirmed) next.delete(key);
          return next;
        });
      }
    }
  }

  // Fallback: clear all resizing entries after 30s, in case the reported slots
  // never lands exactly on the request (e.g. a concurrent restart).
  useEffect(() => {
    if (resizingWorkers.size === 0) return;
    const timer = setTimeout(() => setResizingWorkers(new Map()), 30000);
    return () => clearTimeout(timer);
  }, [resizingWorkers]);

  // Reconcile retiringWorkers: clear when server reports status !== RUNNING or worker disappears.
  // Only runs when server data changes (nodes). A 10s grace period prevents clearing
  // entries that were just added (server hasn't caught up yet).
  useEffect(() => {
    const GRACE_MS = 10_000;
    const now = Date.now();
    setRetiringWorkers((prev) => {
      if (prev.size === 0) return prev;
      const allWorkerKeys = new Set<string>();
      for (const node of nodes) {
        for (const ws of node.worker_summaries || []) {
          if (ws.instance_id) allWorkerKeys.add(`${node.id}:${ws.instance_id}`);
          if (ws.worker_id) allWorkerKeys.add(ws.worker_id);
        }
      }
      const toClear: string[] = [];
      for (const key of prev) {
        if (now - (retiringTimestamps.current.get(key) ?? 0) < GRACE_MS) continue;
        if (!allWorkerKeys.has(key)) {
          toClear.push(key);
          continue;
        }
        for (const node of nodes) {
          for (const ws of node.worker_summaries || []) {
            const wsKey = ws.instance_id ? `${node.id}:${ws.instance_id}` : ws.worker_id;
            if (wsKey === key && ws.status !== 'RUNNING') {
              toClear.push(key);
            }
          }
        }
      }
      if (toClear.length === 0) return prev;
      const next = new Set(prev);
      for (const key of toClear) {
        next.delete(key);
        retiringTimestamps.current.delete(key);
      }
      return next;
    });
  }, [nodes]);

  // Fallback timeout for retiringWorkers: 60s (graceful shutdown takes time)
  useEffect(() => {
    if (retiringWorkers.size === 0) return;
    const timer = setTimeout(() => {
      retiringTimestamps.current.clear();
      setRetiringWorkers(new Set());
    }, 60000);
    return () => clearTimeout(timer);
  }, [retiringWorkers.size]);

  // Fallback timeout for evictingWorkers: the kill confirms within a couple of
  // checkins, after which the row's running count drops on the next poll.
  useEffect(() => {
    if (evictingWorkers.size === 0) return;
    const timer = setTimeout(() => setEvictingWorkers(new Set()), 30000);
    return () => clearTimeout(timer);
  }, [evictingWorkers.size]);

  // Reconcile cleaningUpWorkers, removingWorkers, removingNodes:
  // clear when items disappear from server data. Same render-time
  // previous-prop pattern, keyed on the nodes snapshot.
  const [prevNodesForReconcile, setPrevNodesForReconcile] = useState(nodes);
  if (prevNodesForReconcile !== nodes) {
    setPrevNodesForReconcile(nodes);
    const currentNodeIds = new Set(nodes.map((n) => n.id));
    const currentWorkerKeys = new Set<string>();
    for (const node of nodes) {
      for (const ws of node.worker_summaries || []) {
        if (ws.instance_id) currentWorkerKeys.add(`${node.id}:${ws.instance_id}`);
      }
    }

    if (cleaningUpWorkers.size > 0) {
      const toClear = [...cleaningUpWorkers].filter((k) => !currentWorkerKeys.has(k));
      if (toClear.length > 0) {
        setCleaningUpWorkers((prev) => {
          const next = new Set(prev);
          for (const key of toClear) next.delete(key);
          return next;
        });
      }
    }
    if (removingWorkers.size > 0) {
      const toClear = [...removingWorkers].filter((k) => !currentWorkerKeys.has(k));
      if (toClear.length > 0) {
        setRemovingWorkers((prev) => {
          const next = new Set(prev);
          for (const key of toClear) next.delete(key);
          return next;
        });
      }
    }
    if (stoppingWorkers.size > 0) {
      const toClear: string[] = [];
      for (const key of stoppingWorkers) {
        if (!currentWorkerKeys.has(key)) {
          toClear.push(key);
          continue;
        }
        for (const node of nodes) {
          for (const ws of node.worker_summaries || []) {
            const wsKey = ws.instance_id ? `${node.id}:${ws.instance_id}` : null;
            if (wsKey === key && ws.status === 'STOPPED') {
              toClear.push(key);
            }
          }
        }
      }
      if (toClear.length > 0) {
        setStoppingWorkers((prev) => {
          const next = new Set(prev);
          for (const key of toClear) next.delete(key);
          return next;
        });
      }
    }
    if (restartingWorkers.size > 0) {
      const toClear: string[] = [];
      for (const key of restartingWorkers) {
        if (!currentWorkerKeys.has(key)) {
          toClear.push(key);
          continue;
        }
        for (const node of nodes) {
          for (const ws of node.worker_summaries || []) {
            const wsKey = ws.instance_id ? `${node.id}:${ws.instance_id}` : null;
            if (wsKey === key && ws.status !== 'STOPPED') {
              toClear.push(key);
            }
          }
        }
      }
      if (toClear.length > 0) {
        setRestartingWorkers((prev) => {
          const next = new Set(prev);
          for (const key of toClear) next.delete(key);
          return next;
        });
      }
    }
    if (removingNodes.size > 0) {
      const toClear = [...removingNodes].filter((k) => !currentNodeIds.has(k));
      if (toClear.length > 0) {
        setRemovingNodes((prev) => {
          const next = new Set(prev);
          for (const key of toClear) next.delete(key);
          return next;
        });
      }
    }
  }

  // Fallback timeout for cleaningUpWorkers, removingWorkers, removingNodes: 30s
  useEffect(() => {
    if (cleaningUpWorkers.size === 0) return;
    const timer = setTimeout(() => setCleaningUpWorkers(new Set()), 30000);
    return () => clearTimeout(timer);
  }, [cleaningUpWorkers.size]);
  useEffect(() => {
    if (removingWorkers.size === 0) return;
    const timer = setTimeout(() => setRemovingWorkers(new Set()), 30000);
    return () => clearTimeout(timer);
  }, [removingWorkers.size]);
  useEffect(() => {
    if (stoppingWorkers.size === 0) return;
    const timer = setTimeout(() => setStoppingWorkers(new Set()), 60000);
    return () => clearTimeout(timer);
  }, [stoppingWorkers.size]);
  useEffect(() => {
    if (removingNodes.size === 0) return;
    const timer = setTimeout(() => setRemovingNodes(new Set()), 30000);
    return () => clearTimeout(timer);
  }, [removingNodes.size]);
  useEffect(() => {
    if (restartingWorkers.size === 0) return;
    const timer = setTimeout(() => setRestartingWorkers(new Set()), 30000);
    return () => clearTimeout(timer);
  }, [restartingWorkers.size]);

  const toggleExpanded = (nodeId: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  return (
    <Fragment>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell style={{ width: 48 }} />
              <TableCell>Name</TableCell>
              <TableCell>Account</TableCell>
              <TableCell>Node ID</TableCell>
              <TableCell>State</TableCell>
              <TableCell>OS</TableCell>
              <TableCell>CPU</TableCell>
              <TableCell>Memory</TableCell>
              <TableCell>Disk</TableCell>
              <TableCell>Running Workers</TableCell>
              <TableCell>Slots</TableCell>
              <TableCell>Last Seen</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {nodes.length === 0 && (
              <TableRow>
                <TableCell colSpan={13} align="center">
                  No nodes registered. Start a node agent with: <code>tailor node-agent</code>
                </TableCell>
              </TableRow>
            )}
            {[...nodes]
              .sort((a, b) => a.id.localeCompare(b.id))
              .map((node) => {
                const workers =
                  node.state === 'OFFLINE'
                    ? []
                    : [...(node.worker_summaries || [])].sort(
                        (a, b) =>
                          (a.name || '').localeCompare(b.name || '') ||
                          (a.instance_id || '').localeCompare(b.instance_id || ''),
                      );
                const hasWorkers = workers.length > 0;
                const isProvisioning = provisioningNodeId === node.id;
                // instance id → the worker taking it over, for the "replaced by" tag.
                const replacedBy = new Map<string, string>();
                for (const w of workers) {
                  for (const iid of w.replacing ?? []) {
                    if (w.instance_id) replacedBy.set(iid, w.instance_id);
                  }
                }
                // Banners for in-flight actions on this node's workers.
                // The kebab menu auto-closes after click, so the menu-item
                // label "Retiring…"/"Cleaning up…" is gone before the action
                // completes — the banner is the persistent feedback. Mirrors
                // the "Starting new worker..." pattern used for provisioning.
                const inFlightBanners: { key: string; label: string }[] = [];
                for (const w of workers) {
                  const instanceKey = w.instance_id ? `${node.id}:${w.instance_id}` : null;
                  const workerId = w.worker_id || null;
                  const displayName = w.name || w.instance_id || w.worker_id || '?';
                  // Stable per-worker id so React keys never collide when two
                  // workers under the same node share a name (e.g. ASG-managed
                  // workers all named `w-ec2`, distinguished only by instance_id).
                  const bannerId = instanceKey || workerId || displayName;
                  const retiringHit =
                    (instanceKey && retiringWorkers.has(instanceKey)) || (workerId && retiringWorkers.has(workerId));
                  if (retiringHit) {
                    inFlightBanners.push({ key: `retire-${bannerId}`, label: `Retiring worker ${displayName}…` });
                  }
                  if (instanceKey && upgradingWorkers.has(instanceKey)) {
                    inFlightBanners.push({ key: `upgrade-${bannerId}`, label: `Upgrading worker ${displayName}…` });
                  }
                  if (instanceKey && stoppingWorkers.has(instanceKey)) {
                    inFlightBanners.push({ key: `stop-${bannerId}`, label: `Stopping worker ${displayName}…` });
                  }
                  if (instanceKey && cleaningUpWorkers.has(instanceKey)) {
                    inFlightBanners.push({
                      key: `cleanup-${bannerId}`,
                      label: `Cleaning up worker ${displayName}…`,
                    });
                  }
                  if (instanceKey && removingWorkers.has(instanceKey)) {
                    inFlightBanners.push({ key: `remove-${bannerId}`, label: `Removing worker ${displayName}…` });
                  }
                  if (instanceKey && restartingWorkers.has(instanceKey)) {
                    inFlightBanners.push({
                      key: `restart-${bannerId}`,
                      label: `Restarting worker ${displayName}…`,
                    });
                  }
                }
                const showWorkerSection = hasWorkers || isProvisioning || inFlightBanners.length > 0;
                const isExpanded = expandedNodes.has(node.id);
                const runningWorkerCount = workers.filter((w) => w.status === 'RUNNING').length;
                const runningTasks = workers
                  .filter((w) => w.status === 'RUNNING' || w.status === 'RETIRING')
                  .reduce((sum, w) => sum + (w.running_tasks ?? 0), 0);
                const heldSlots = workers
                  .filter((w) => w.status === 'RUNNING' && !(w.instance_id && replacedBy.has(w.instance_id)))
                  .reduce((sum, w) => sum + (w.budget ?? w.slots ?? 0), 0);

                return (
                  <Fragment key={node.id}>
                    <TableRow>
                      <TableCell>
                        {showWorkerSection && (
                          <IconButton size="small" onClick={() => toggleExpanded(node.id)}>
                            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell>{node.name || '-'}</TableCell>
                      <TableCell>{node.account_name || '-'}</TableCell>
                      <TableCell
                        css={css`
                          font-family: monospace;
                          font-size: 0.85rem;
                        `}
                      >
                        <Tooltip title={node.node_id}>
                          <span
                            style={{ cursor: 'pointer' }}
                            onClick={() => navigator.clipboard.writeText(node.node_id).catch(() => {})}
                          >
                            {node.node_id.substring(0, 8)}...
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip label={node.state} color={node.state === 'ONLINE' ? 'success' : 'default'} size="small" />
                        {node.drain && (
                          <Tooltip title={`Drain requested ${formatLastSeen(node.drain.requested_at)}`}>
                            <Box sx={{ mt: 0.5 }}>
                              <Chip label="Drained" color="warning" size="small" />
                              <div
                                css={css`
                                  font-size: 0.75rem;
                                  color: #666;
                                  margin-top: 2px;
                                `}
                              >
                                {describeDrain(node.drain)}
                              </div>
                            </Box>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        <div>
                          {String(node.os_info?.platform ?? '-')} {String(node.os_info?.architecture ?? '')}
                        </div>
                        {node.pytailor_version && (
                          <div
                            css={css`
                              font-size: 0.75rem;
                              color: #666;
                            `}
                          >
                            pytailor {node.pytailor_version}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {node.state === 'ONLINE' && node.metrics?.cpu_percent != null
                          ? `${node.metrics.cpu_percent}% / ${node.resources?.cpu_count ?? '?'} cores`
                          : String(node.resources?.cpu_count ?? '-')}
                      </TableCell>
                      <TableCell>
                        {node.state === 'ONLINE' && node.metrics?.memory_percent != null
                          ? `${node.metrics.memory_percent}% / ${node.resources?.memory_gb ?? '?'} GB`
                          : node.resources?.memory_gb
                            ? `${node.resources.memory_gb} GB`
                            : '-'}
                      </TableCell>
                      <TableCell>
                        {node.state === 'ONLINE' && node.metrics?.disk_percent != null
                          ? `${node.metrics.disk_percent}% / ${node.metrics.disk_total_gb} GB`
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {node.state === 'OFFLINE' ? (
                          '-'
                        ) : (
                          <Tooltip title="Running workers (tasks running on running and retiring workers / slots the running workers hold or grow into).">
                            <span>{`${runningWorkerCount} (${runningTasks} / ${heldSlots} slots running)`}</span>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        {node.state === 'OFFLINE' ? (
                          '-'
                        ) : (
                          <SlotsCell inUse={node.slots_in_use ?? heldSlots} budget={node.slot_budget ?? null} />
                        )}
                      </TableCell>
                      <TableCell>{formatLastSeen(node.last_seen)}</TableCell>
                      <TableCell>
                        <NodeActionsMenu
                          node={node}
                          pendingFlags={{
                            isDraining: drainTarget?.node.id === node.id && drainTarget.mode === 'drain',
                            isResuming: drainTarget?.node.id === node.id && drainTarget.mode === 'resume',
                            isRemoving: removingNodes.has(node.id),
                          }}
                          onStartWorker={() => onStartWorker(node.id)}
                          onRecycleLocalFiles={() =>
                            setRecycleTarget({ nodeAgentId: node.id, nodeName: node.name || node.node_id })
                          }
                          onDrain={() => setDrainTarget({ node, mode: 'drain' })}
                          onResume={() => setDrainTarget({ node, mode: 'resume' })}
                          onRemove={() => {
                            setRemovingNodes((prev) => new Set(prev).add(node.id));
                            deleteNodeAgent(node.id).then(
                              () => onMutate(),
                              (error) => {
                                console.error('Failed to remove node:', error);
                                setRemovingNodes((prev) => {
                                  const next = new Set(prev);
                                  next.delete(node.id);
                                  return next;
                                });
                              },
                            );
                          }}
                        />
                      </TableCell>
                    </TableRow>

                    {showWorkerSection && (
                      <TableRow>
                        <TableCell colSpan={13} style={{ paddingBottom: 0, paddingTop: 0 }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box
                              sx={{
                                margin: 2,
                                padding: 2,
                                backgroundColor: 'grey.50',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'grey.200',
                              }}
                            >
                              <Typography variant="subtitle2" gutterBottom component="div">
                                Workers
                              </Typography>
                              {(() => {
                                const banners = [
                                  ...(isProvisioning ? [{ key: 'provision', label: 'Starting new worker...' }] : []),
                                  ...inFlightBanners,
                                ];
                                return banners.map((b, i) => {
                                  const isLast = i === banners.length - 1;
                                  return (
                                    <Alert
                                      key={b.key}
                                      severity="info"
                                      icon={<CircularProgress size={16} />}
                                      sx={{ mb: isLast ? (hasWorkers ? 2 : 0) : 1 }}
                                    >
                                      {b.label}
                                    </Alert>
                                  );
                                });
                              })()}
                              {hasWorkers && (
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Name</TableCell>
                                      <TableCell>Workdir</TableCell>
                                      <TableCell>Worker ID</TableCell>
                                      <TableCell>PID</TableCell>
                                      <TableCell>Status</TableCell>
                                      <TableCell>Running Slots</TableCell>
                                      <TableCell>Recycle</TableCell>
                                      <TableCell>Software</TableCell>
                                      <TableCell>pytailor</TableCell>
                                      <TableCell>Python</TableCell>
                                      <TableCell>Projects</TableCell>
                                      <TableCell>Capabilities</TableCell>
                                      <TableCell>Last Seen</TableCell>
                                      <TableCell>Actions</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {workers.map((worker) => {
                                      const errorMsg =
                                        worker.start_error || worker.cleanup_error || worker.upgrade_error;
                                      const isFailedStatus =
                                        worker.status === 'START_FAILED' ||
                                        worker.status === 'CLEANUP_FAILED' ||
                                        worker.status === 'UPGRADE_FAILED';
                                      const actionError = worker.instance_id
                                        ? actionErrors.get(`${node.id}:${worker.instance_id}`)
                                        : undefined;
                                      const showInlineError = (!!errorMsg && isFailedStatus) || !!actionError;
                                      const statusHint =
                                        errorMsg ||
                                        (worker.status === 'STOPPED' && worker.last_exit_reason
                                          ? `Last exit: ${worker.last_exit_reason}`
                                          : worker.start_warning);
                                      const errorLabel = actionError
                                        ? 'Refused'
                                        : worker.status === 'START_FAILED'
                                          ? 'Failed to start'
                                          : worker.status === 'UPGRADE_FAILED'
                                            ? 'Upgrade failed'
                                            : 'Cleanup failed';
                                      const rowKey = worker.instance_id || worker.worker_id || worker.name;
                                      return (
                                        <Fragment key={rowKey}>
                                          <TableRow>
                                            <TableCell>
                                              {worker.name}
                                              {worker.instance_id && (
                                                <Tooltip title="The node agent's id for this worker; what other rows refer to.">
                                                  <Chip
                                                    label={worker.instance_id}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ ml: 1, fontSize: '0.7rem', fontFamily: 'monospace' }}
                                                  />
                                                </Tooltip>
                                              )}
                                              {worker.instance_id && replacedBy.has(worker.instance_id) && (
                                                <Tooltip
                                                  title={`${replacedBy.get(worker.instance_id)} takes over this worker's slots as its running tasks finish; it is removed when it has drained.`}
                                                >
                                                  <Chip
                                                    label={`replaced by ${replacedBy.get(worker.instance_id)}`}
                                                    size="small"
                                                    color="warning"
                                                    variant="outlined"
                                                    sx={{ ml: 0.5, fontSize: '0.7rem' }}
                                                  />
                                                </Tooltip>
                                              )}
                                            </TableCell>
                                            <TableCell>{worker.workdir_name || '-'}</TableCell>
                                            <TableCell
                                              css={css`
                                                font-family: monospace;
                                                font-size: 0.85rem;
                                              `}
                                            >
                                              {worker.worker_id &&
                                              (worker.status === 'RUNNING' || worker.status === 'RETIRING') ? (
                                                <Tooltip title={worker.worker_id}>
                                                  <span
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() =>
                                                      navigator.clipboard.writeText(worker.worker_id!).catch(() => {})
                                                    }
                                                  >
                                                    {worker.worker_id.substring(0, 8)}
                                                    ...
                                                  </span>
                                                </Tooltip>
                                              ) : (
                                                '-'
                                              )}
                                            </TableCell>
                                            <TableCell>{worker.pid != null ? worker.pid : '-'}</TableCell>
                                            <TableCell>
                                              {(() => {
                                                const workerKey = worker.instance_id
                                                  ? `${node.id}:${worker.instance_id}`
                                                  : worker.worker_id;
                                                const displayStatus =
                                                  worker.status === 'RUNNING' &&
                                                  workerKey &&
                                                  retiringWorkers.has(workerKey)
                                                    ? 'RETIRING'
                                                    : worker.status === 'RUNNING' &&
                                                        workerKey &&
                                                        upgradingWorkers.has(workerKey)
                                                      ? 'UPGRADING'
                                                      : worker.status;
                                                return (
                                                  <Tooltip
                                                    title={statusHint || ''}
                                                    disableHoverListener={!statusHint || showInlineError}
                                                  >
                                                    <Chip
                                                      label={displayStatus}
                                                      color={
                                                        displayStatus === 'RUNNING'
                                                          ? 'success'
                                                          : displayStatus === 'STARTING' ||
                                                              displayStatus === 'UPGRADING'
                                                            ? 'info'
                                                            : displayStatus === 'CLEANUP_FAILED' ||
                                                                displayStatus === 'START_FAILED' ||
                                                                displayStatus === 'UPGRADE_FAILED'
                                                              ? 'error'
                                                              : displayStatus === 'RETIRING'
                                                                ? 'info'
                                                                : displayStatus === 'MISSING'
                                                                  ? 'warning'
                                                                  : 'default'
                                                      }
                                                      size="small"
                                                    />
                                                  </Tooltip>
                                                );
                                              })()}
                                            </TableCell>
                                            <TableCell>
                                              {worker.worker_id &&
                                              (worker.status === 'RUNNING' || worker.status === 'RETIRING') ? (
                                                <Tooltip title="View running tasks">
                                                  <MuiLink
                                                    component="button"
                                                    type="button"
                                                    onClick={() =>
                                                      setRunningTasksTarget({
                                                        workerId: worker.worker_id!,
                                                        workerName: worker.name,
                                                      })
                                                    }
                                                  >
                                                    {worker.running_tasks ?? 0} / {worker.slots}
                                                  </MuiLink>
                                                </Tooltip>
                                              ) : (
                                                <>
                                                  {worker.running_tasks ?? 0} / {worker.slots}
                                                </>
                                              )}
                                              {(worker.overshoot ?? 0) > 0 && worker.status !== 'RETIRING' && (
                                                <Tooltip
                                                  title={`Runs up to ${worker.overshoot} FAST-lane tasks above its ${worker.slots} slots; BATCH and BACKGROUND tasks never use them, and the node slot budget does not count them.`}
                                                >
                                                  <Typography
                                                    component="span"
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ ml: 0.5 }}
                                                  >
                                                    +{worker.overshoot}
                                                  </Typography>
                                                </Tooltip>
                                              )}
                                              {worker.replacing &&
                                                worker.replacing.length > 0 &&
                                                worker.budget != null &&
                                                worker.budget !== worker.slots && (
                                                  <Tooltip title="Slots this worker holds now, of the budget it grows into as the workers it replaces drain.">
                                                    <Typography
                                                      component="span"
                                                      variant="caption"
                                                      color="text.secondary"
                                                      sx={{ ml: 0.5 }}
                                                    >
                                                      of {worker.budget}
                                                    </Typography>
                                                  </Tooltip>
                                                )}
                                              {worker.replacing && worker.replacing.length > 0 && (
                                                <Tooltip
                                                  title={`Taking over from ${worker.replacing.join(', ')}; their slots move here as their running tasks finish.`}
                                                >
                                                  <Typography
                                                    component="span"
                                                    variant="caption"
                                                    color="info.main"
                                                    sx={{ ml: 0.5 }}
                                                  >
                                                    replacing {worker.replacing.join(', ')}
                                                  </Typography>
                                                </Tooltip>
                                              )}
                                              {workerDrainSurplus(worker) > 0 && (
                                                <Tooltip title="Scaling down — surplus slots finish their current task, then release.">
                                                  <Typography
                                                    component="span"
                                                    variant="caption"
                                                    color="warning.main"
                                                    sx={{ ml: 0.5 }}
                                                  >
                                                    (draining)
                                                  </Typography>
                                                </Tooltip>
                                              )}
                                            </TableCell>
                                            <TableCell>
                                              <Tooltip
                                                title={
                                                  worker.max_tasks_per_process == null
                                                    ? 'Recycle budget from the worker config default'
                                                    : worker.max_tasks_per_process === 0
                                                      ? 'Task processes are never recycled'
                                                      : `Each task process recycles after ${worker.max_tasks_per_process} task(s)`
                                                }
                                              >
                                                <Chip
                                                  label={`${worker.max_tasks_per_process ?? 'default'}`}
                                                  color="secondary"
                                                  size="small"
                                                  variant="outlined"
                                                />
                                              </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                              {(() => {
                                                const software = describeSoftware(worker);
                                                return (
                                                  <Tooltip title={software.tooltip}>
                                                    <Box
                                                      component="span"
                                                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}
                                                    >
                                                      <Typography
                                                        component="span"
                                                        variant="body2"
                                                        sx={{ fontFamily: 'monospace' }}
                                                      >
                                                        {software.label}
                                                      </Typography>
                                                      {software.origin && (
                                                        <Chip label={software.origin} size="small" variant="outlined" />
                                                      )}
                                                    </Box>
                                                  </Tooltip>
                                                );
                                              })()}
                                            </TableCell>
                                            <TableCell>{worker.pytailor_version || '-'}</TableCell>
                                            <TableCell>{worker.venv_python_version || '-'}</TableCell>
                                            <TableCell>
                                              <ProjectFilterCell
                                                projectIds={worker.project_id_filter ?? []}
                                                projectNamesById={projectNamesById}
                                                projectsLoaded={projectsLoaded}
                                              />
                                            </TableCell>
                                            <TableCell>
                                              {worker.capabilities?.length ? worker.capabilities.join(', ') : '-'}
                                            </TableCell>
                                            <TableCell>
                                              {worker.last_seen ? formatLastSeen(worker.last_seen) : '-'}
                                            </TableCell>
                                            <TableCell>
                                              {(() => {
                                                const instanceKey = worker.instance_id
                                                  ? `${node.id}:${worker.instance_id}`
                                                  : null;
                                                const workerId = worker.worker_id || null;
                                                return (
                                                  <WorkerActionsMenu
                                                    worker={worker}
                                                    nodeDrained={Boolean(node.drain)}
                                                    onViewTasks={
                                                      worker.worker_id
                                                        ? () =>
                                                            setRunningTasksTarget({
                                                              workerId: worker.worker_id!,
                                                              workerName: worker.name,
                                                            })
                                                        : undefined
                                                    }
                                                    onStartFromTemplate={() => onStartWorker(node.id, worker)}
                                                    pendingFlags={{
                                                      isRetiring: instanceKey
                                                        ? retiringWorkers.has(instanceKey)
                                                        : workerId
                                                          ? retiringWorkers.has(workerId)
                                                          : false,
                                                      isStopping: instanceKey
                                                        ? stoppingWorkers.has(instanceKey)
                                                        : false,
                                                      isUpgrading:
                                                        worker.status === 'UPGRADING' ||
                                                        (instanceKey ? upgradingWorkers.has(instanceKey) : false),
                                                      isCleaningUp: instanceKey
                                                        ? cleaningUpWorkers.has(instanceKey)
                                                        : false,
                                                      isRemoving: instanceKey
                                                        ? removingWorkers.has(instanceKey)
                                                        : false,
                                                      isRestarting: instanceKey
                                                        ? restartingWorkers.has(instanceKey)
                                                        : false,
                                                      isResizing: workerId ? resizingWorkers.has(workerId) : false,
                                                      isEvicting: workerId ? evictingWorkers.has(workerId) : false,
                                                    }}
                                                    onRetire={
                                                      worker.worker_id
                                                        ? () => {
                                                            const wid = worker.worker_id!;
                                                            setRetireTarget({
                                                              workerId: wid,
                                                              workerName: worker.name,
                                                              packageName: worker.package_name,
                                                              packageVersion:
                                                                worker.installed_version || worker.package_version,
                                                              trackingKey: worker.instance_id
                                                                ? `${node.id}:${worker.instance_id}`
                                                                : wid,
                                                            });
                                                          }
                                                        : undefined
                                                    }
                                                    onStop={
                                                      worker.instance_id
                                                        ? () =>
                                                            setStopTarget({
                                                              nodeAgentId: node.id,
                                                              instanceId: worker.instance_id!,
                                                              workerName: worker.name,
                                                            })
                                                        : undefined
                                                    }
                                                    onUpgrade={
                                                      worker.instance_id &&
                                                      (worker.package_name || worker.software || worker.software_ref)
                                                        ? () =>
                                                            setUpgradeTarget({
                                                              nodeAgentId: node.id,
                                                              instanceId: worker.instance_id!,
                                                              workerName: worker.name,
                                                              currentPackageName: worker.package_name,
                                                              currentVersion:
                                                                worker.installed_version || worker.package_version,
                                                              currentSoftware: worker.software,
                                                              currentSoftwareRef: worker.software_ref,
                                                              workdirName: worker.workdir_name,
                                                              worker,
                                                            })
                                                        : undefined
                                                    }
                                                    onResize={
                                                      worker.worker_id
                                                        ? () =>
                                                            setResizeTarget({
                                                              workerId: worker.worker_id!,
                                                              workerName: worker.name,
                                                              currentSlots: worker.budget ?? worker.slots ?? 0,
                                                              heldSlots: worker.slots ?? 0,
                                                              replacing: worker.replacing,
                                                              slotBudget: slotBudgetOf(node),
                                                              currentOvershoot: worker.overshoot,
                                                              overshootSupported: Boolean(worker.supports_overshoot),
                                                            })
                                                        : undefined
                                                    }
                                                    onEvict={
                                                      worker.worker_id
                                                        ? () => {
                                                            const running = worker.running_tasks ?? 0;
                                                            const isRetiring = worker.status === 'RETIRING';
                                                            setEvictTarget({
                                                              workerId: worker.worker_id!,
                                                              workerName: worker.name,
                                                              isRetiring,
                                                              evictCount: isRetiring
                                                                ? running
                                                                : workerDrainSurplus(worker),
                                                            });
                                                          }
                                                        : undefined
                                                    }
                                                    onCleanup={
                                                      worker.instance_id
                                                        ? () =>
                                                            setCleanupTarget({
                                                              nodeAgentId: node.id,
                                                              instanceId: worker.instance_id!,
                                                              workerName: worker.name,
                                                            })
                                                        : undefined
                                                    }
                                                    onRemoveManaged={
                                                      worker.instance_id
                                                        ? () =>
                                                            setRemoveTarget({
                                                              nodeAgentId: node.id,
                                                              instanceId: worker.instance_id!,
                                                              workerName: worker.name,
                                                            })
                                                        : undefined
                                                    }
                                                    onRestart={
                                                      worker.instance_id
                                                        ? () => {
                                                            const iid = worker.instance_id!;
                                                            const key = `${node.id}:${iid}`;
                                                            setRestartingWorkers((prev) => new Set(prev).add(key));
                                                            setActionErrors((prev) => {
                                                              const next = new Map(prev);
                                                              next.delete(key);
                                                              return next;
                                                            });
                                                            sendNodeCommand(node.id, 'restart_worker', {
                                                              instance_id: iid,
                                                            }).then(
                                                              () => onMutate(),
                                                              async (error) => {
                                                                console.error('Failed to restart worker:', error);
                                                                const reason = await errorMessage(error);
                                                                setActionErrors((prev) =>
                                                                  new Map(prev).set(key, `Restart refused: ${reason}`),
                                                                );
                                                                setRestartingWorkers((prev) => {
                                                                  const next = new Set(prev);
                                                                  next.delete(key);
                                                                  return next;
                                                                });
                                                              },
                                                            );
                                                          }
                                                        : undefined
                                                    }
                                                  />
                                                );
                                              })()}
                                            </TableCell>
                                          </TableRow>
                                          {showInlineError && (
                                            <TableRow>
                                              <TableCell
                                                colSpan={14}
                                                sx={{ py: 0.5, borderBottom: 0, backgroundColor: 'transparent' }}
                                              >
                                                <Alert
                                                  severity="error"
                                                  variant="outlined"
                                                  sx={{ mb: 1, alignItems: 'flex-start' }}
                                                  onClose={
                                                    actionError
                                                      ? () =>
                                                          setActionErrors((prev) => {
                                                            const next = new Map(prev);
                                                            next.delete(`${node.id}:${worker.instance_id}`);
                                                            return next;
                                                          })
                                                      : undefined
                                                  }
                                                >
                                                  <Typography
                                                    variant="body2"
                                                    component="div"
                                                    sx={{ fontWeight: 600, mb: 0.5 }}
                                                  >
                                                    {errorLabel}
                                                  </Typography>
                                                  <Typography
                                                    variant="body2"
                                                    component="pre"
                                                    sx={{
                                                      m: 0,
                                                      whiteSpace: 'pre-wrap',
                                                      wordBreak: 'break-word',
                                                      fontFamily:
                                                        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                                                      fontSize: '0.8rem',
                                                    }}
                                                  >
                                                    {actionError ?? errorMsg}
                                                  </Typography>
                                                </Alert>
                                              </TableCell>
                                            </TableRow>
                                          )}
                                        </Fragment>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      {runningTasksTarget && (
        <RunningTasksDrawer
          workerId={runningTasksTarget.workerId}
          workerName={runningTasksTarget.workerName}
          onClose={() => setRunningTasksTarget(null)}
        />
      )}
      {cleanupTarget && (
        <DisposeWorkerDialog
          action="cleanup"
          nodeAgentId={cleanupTarget.nodeAgentId}
          instanceId={cleanupTarget.instanceId}
          workerName={cleanupTarget.workerName}
          onClose={() => setCleanupTarget(null)}
          onConfirm={() => {
            setCleaningUpWorkers((prev) =>
              new Set(prev).add(`${cleanupTarget.nodeAgentId}:${cleanupTarget.instanceId}`),
            );
            onMutate();
          }}
        />
      )}
      {removeTarget && (
        <DisposeWorkerDialog
          action="remove"
          nodeAgentId={removeTarget.nodeAgentId}
          instanceId={removeTarget.instanceId}
          workerName={removeTarget.workerName}
          onClose={() => setRemoveTarget(null)}
          onConfirm={() => {
            setRemovingWorkers((prev) => new Set(prev).add(`${removeTarget.nodeAgentId}:${removeTarget.instanceId}`));
            onMutate();
          }}
        />
      )}
      {stopTarget && (
        <StopWorkerDialog
          nodeAgentId={stopTarget.nodeAgentId}
          instanceId={stopTarget.instanceId}
          workerName={stopTarget.workerName}
          onClose={() => setStopTarget(null)}
          onConfirm={() => {
            setStoppingWorkers((prev) => new Set(prev).add(`${stopTarget.nodeAgentId}:${stopTarget.instanceId}`));
            onMutate();
          }}
        />
      )}
      {drainTarget && (
        <NodeDrainDialog
          key={`${drainTarget.node.id}-${drainTarget.mode}`}
          node={nodes.find((n) => n.id === drainTarget.node.id) ?? drainTarget.node}
          mode={drainTarget.mode}
          onClose={() => setDrainTarget(null)}
          onDone={onMutate}
        />
      )}
      {recycleTarget && (
        <RecycleLocalFilesDialog
          nodeAgentId={recycleTarget.nodeAgentId}
          nodeName={recycleTarget.nodeName}
          onClose={() => setRecycleTarget(null)}
        />
      )}
      {retireTarget && (
        <RetireWorkerDialog
          workerId={retireTarget.workerId}
          workerName={retireTarget.workerName}
          packageName={retireTarget.packageName}
          packageVersion={retireTarget.packageVersion}
          onClose={() => setRetireTarget(null)}
          onConfirm={() => {
            retiringTimestamps.current.set(retireTarget.trackingKey, Date.now());
            setRetiringWorkers((prev) => new Set(prev).add(retireTarget.trackingKey));
            onMutate();
          }}
        />
      )}
      {upgradeTarget &&
        (() => {
          const node = nodes.find((n) => n.id === upgradeTarget.nodeAgentId);
          const allWorkers = node?.worker_summaries || [];
          const affected = upgradeTarget.workdirName
            ? allWorkers.filter((ws) => ws.workdir_name === upgradeTarget.workdirName && ws.instance_id)
            : allWorkers.filter((ws) => ws.instance_id === upgradeTarget.instanceId);
          const row = upgradeTarget.worker;
          const group = row ? groupMembers(allWorkers, groupParamsOf(row)) : [];
          return (
            <UpgradeWorkerDialog
              nodeAgentId={upgradeTarget.nodeAgentId}
              instanceId={upgradeTarget.instanceId}
              workerName={upgradeTarget.workerName}
              currentPackageName={upgradeTarget.currentPackageName}
              currentVersion={upgradeTarget.currentVersion}
              currentSoftware={upgradeTarget.currentSoftware}
              currentSoftwareRef={upgradeTarget.currentSoftwareRef}
              affectedWorkers={affected.map((ws) => ({ name: ws.name, runningTasks: ws.running_tasks ?? 0 }))}
              worker={row}
              groupMembers={group}
              agentPytailorVersion={node?.pytailor_version ?? null}
              slotBudget={slotBudgetOf(node)}
              onClose={() => setUpgradeTarget(null)}
              onConfirm={(mode: UpgradeMode) => {
                if (mode === 'rolling') {
                  // The new generation is a new row: same feedback as Start worker.
                  if (onProvisioning) onProvisioning(upgradeTarget.nodeAgentId);
                  else onMutate();
                  return;
                }
                const nodeId = upgradeTarget.nodeAgentId;
                setUpgradingWorkers((prev) => {
                  const next = new Map(prev);
                  for (const ws of affected) {
                    const key = `${nodeId}:${ws.instance_id}`;
                    next.set(key, ws.software || ws.installed_version || ws.package_version);
                  }
                  return next;
                });
                onMutate();
              }}
            />
          );
        })()}
      {resizeTarget && (
        <ResizeWorkerDialog
          workerId={resizeTarget.workerId}
          workerName={resizeTarget.workerName}
          currentSlots={resizeTarget.currentSlots}
          heldSlots={resizeTarget.heldSlots}
          replacing={resizeTarget.replacing}
          slotBudget={resizeTarget.slotBudget}
          currentOvershoot={resizeTarget.currentOvershoot}
          overshootSupported={resizeTarget.overshootSupported}
          onClose={() => setResizeTarget(null)}
          onConfirm={(slots) => {
            setResizingWorkers((prev) => new Map(prev).set(resizeTarget.workerId, slots));
            onMutate();
          }}
        />
      )}
      {evictTarget && (
        <EvictWorkerTasksDialog
          workerId={evictTarget.workerId}
          workerName={evictTarget.workerName}
          evictCount={evictTarget.evictCount}
          isRetiring={evictTarget.isRetiring}
          onClose={() => setEvictTarget(null)}
          onConfirm={() => {
            setEvictingWorkers((prev) => new Set(prev).add(evictTarget.workerId));
            onMutate();
          }}
        />
      )}
    </Fragment>
  );
}
